import {inject, Injectable} from './di';
import {AppInitializeService} from './app-initialize-service';
import {BehaviorSubject} from 'rxjs';
import {Logger} from './logger';

@Injectable()
export class NetworkInfoService implements AppInitializeService {
  private readonly logger = inject(Logger);

  public readonly networkInfo$ = new BehaviorSubject<TvNetworkInfo>({});

  /**
   * Get TV MAC address.
   */
  public get mac() {
    return this.networkInfo$.getValue()?.mac;
  }

  /**
   * Get current TV IP address.
   * This may change while the app is running. For example, if the user changes the TV's network settings or plugs or
   * unplugs the LAN cable after Hoteza TV initialization.
   */
  public get ip() {
    return this.networkInfo$.getValue()?.ip;
  }

  /**
   * Get current TV network type
   * This may change while the app is running. For example, if the user changes the TV's network settings or plugs or
   * unplugs the LAN cable after Hoteza TV initialization.
   */
  public get type() {
    return this.networkInfo$.getValue()?.type;
  }

  constructor() {
    window.tv_mac = '';
    window.tv_ip = '';
  }

  public async init(): Promise<boolean> {
    try {
      if (!window._tv_get_network_info || typeof window._tv_get_network_info !== 'function') {
        this.logger.debug('[NetworkInfoService] _tv_get_network_info is not defined in vendor module');
        return true;
      }

      const networkInfo = (await Promise.resolve(window._tv_get_network_info())) as TvNetworkInfo;

      if (networkInfo) {
        this.networkInfo$.next({
          ip: networkInfo.ip || undefined,
          mac: networkInfo.mac || undefined,
          type: networkInfo.type || undefined,
        });
        if (networkInfo.ip) {
          this.logger.log('[App] IP address: ' + networkInfo.ip);
        }
        if (networkInfo.mac) {
          this.logger.log('[App] MAC address: ' + networkInfo.mac);
        }
        if (networkInfo.type) {
          this.logger.log('[App] Network connection type: ' + networkInfo.type);
        }

        window.tv_mac = networkInfo.mac;
        window.tv_ip = networkInfo.ip;
      } else {
        this.networkInfo$.next({});
      }
    } catch (err) {
      this.logger.error('[NetworkInfoService] _tv_get_network_info vendor module function execution was failed', err);
    }

    return true;
  }
}

export interface TvNetworkInfo {
  /**
   * IP address of current TV.
   */
  ip?: string;

  /**
   * MAC address of current TV.
   */
  mac?: string;

  /**
   * Connection type.
   */
  type?: TvNetworkType;
}

export enum TvNetworkType {
  Ethernet = 'ETH',
  WiFi = 'WIFI',
  Unknown = 'UNKNOWN',
}
